import axios from 'axios';

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api',
  timeout: 90000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors and token refresh
api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 403 Forbidden - User account deactivated
    if (error.response?.status === 403) {
      const errorMessage = error.response?.data?.message || '';
      
      // Check if it's an inactive user error
      if (errorMessage.includes('inactive') || errorMessage.includes('deactivated')) {
        // Clear all auth data
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        
        // Show alert to user
        alert('Your account has been deactivated by admin. Please contact support.');
        
        // Redirect to login
        window.location.href = '/login';
        return Promise.reject(new Error(errorMessage));
      }
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Try to refresh token using refresh token from localStorage
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(
            `${api.defaults.baseURL}/auth/refresh-token`, 
            { refreshToken },
            {
              headers: {
                'Content-Type': 'application/json'
              }
            }
          );
          
          const newToken = response.data?.data?.token || response.data?.token;
          if (newToken) {
            localStorage.setItem('token', newToken);
            
            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return api(originalRequest);
          }
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Handle other errors
    const errorData = error.response?.data;
    const errorMessage = errorData?.message || error.message || 'An error occurred';
    
    // Create error object with full details
    const errorObj = new Error(errorMessage);
    errorObj.errors = errorData?.errors; // Pass through validation errors array
    errorObj.statusCode = error.response?.status;
    
    return Promise.reject(errorObj);
  }
);

export default api;